import React, { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import { Select, notification } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { downloadJSONasCSV } from 'helpers/download-file'
import { batchClearPaymentAssignmentStatus } from '../../api/payment-assignment-api-client'
import {
  BatchClearPaymentAssignmentResultItemDto,
  BulkClearStatus,
} from '../../api/dto/batch-clear-payment-assignment.dto'
import { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'
import { getDefaultPaymentPeriod } from '../PaymentAssignment/hooks/usePaymentAssignmentFilters'

const { Title, Text } = Typography

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const ALLOWED_STATUSES: BulkClearStatus[] = [
  PaymentAssignmentStatusId.PAYMENT_CLEARED,
  PaymentAssignmentStatusId.AP_CONFIRMED,
  PaymentAssignmentStatusId.BOT_CLEARED,
]

interface ParsedRow {
  key: string
  benefitId: string
  status: BulkClearStatus | ''
}

type Step = 'upload' | 'preview' | 'results'

interface BatchProgress {
  sent: number
  total: number
  currentBatch: number
  totalBatches: number
}

interface BulkClearResults {
  /** Aggregated per-payment-assignment results returned from server */
  results: BatchClearPaymentAssignmentResultItemDto[]
  /**
   * Benefit IDs we sent that the server did not return any result for.
   * Most likely they had no payment assignment for the chosen month/year.
   */
  skippedBenefitIds: string[]
  /** Errors raised at the chunk level (network / validation failures). */
  chunkErrors: { benefitIds: string[]; message: string }[]
}

// ---------------------------------------------------------------------------
// CSV configuration
// ---------------------------------------------------------------------------

const CSV_HEADERS = ['benefitId', 'status'] as const

const EXAMPLE_ROWS: Record<(typeof CSV_HEADERS)[number], string>[] = [
  {
    benefitId: 'benefit-cleared-example-id',
    status: PaymentAssignmentStatusId.PAYMENT_CLEARED,
  },
  {
    benefitId: 'benefit-ap-confirmed-example-id',
    status: PaymentAssignmentStatusId.AP_CONFIRMED,
  },
  {
    benefitId: 'benefit-bot-cleared-example-id',
    status: PaymentAssignmentStatusId.BOT_CLEARED,
  },
]

const CHUNK_SIZE = 100
const MAX_CSV_ROWS = 20000

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const isAllowedStatus = (value: string): value is BulkClearStatus =>
  (ALLOWED_STATUSES as string[]).includes(value)

const getRowErrors = (row: ParsedRow): string[] => {
  const errors: string[] = []
  if (!row.benefitId.trim()) errors.push('Benefit ID is required')
  if (!row.status) {
    errors.push(`Status is required (${ALLOWED_STATUSES.join(' or ')})`)
  } else if (!isAllowedStatus(row.status)) {
    errors.push(`Status must be ${ALLOWED_STATUSES.join(' or ')}`)
  }
  return errors
}

const isValidRow = (row: ParsedRow): boolean => getRowErrors(row).length === 0

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PaymentAssignmentBulkClearUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step>('upload')
  const [period, setPeriod] = useState<Dayjs>(() =>
    dayjs(getDefaultPaymentPeriod(), 'YYYY-MM').startOf('month')
  )
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [results, setResults] = useState<BulkClearResults | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const cancelledRef = useRef(false)
  const [batchProgress, setBatchProgress] = useState<BatchProgress>({
    sent: 0,
    total: 0,
    currentBatch: 0,
    totalBatches: 0,
  })

  // Monotonic counter so row keys stay unique even after add/delete/CSV reload
  const nextRowKeyRef = useRef(0)
  const allocateRowKey = useCallback(() => {
    const id = nextRowKeyRef.current
    nextRowKeyRef.current = id + 1
    return `r-${id}`
  }, [])
  const makeEmptyRow = useCallback(
    (): ParsedRow => ({ key: allocateRowKey(), benefitId: '', status: '' }),
    [allocateRowKey]
  )

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleDownloadTemplate = () => {
    downloadJSONasCSV(
      EXAMPLE_ROWS,
      'payment-assignment-bulk-clear-template.csv'
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setParseError(null)

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setParseError(
            `CSV parse error: ${result.errors.map((err) => err.message).join(', ')}`
          )
          return
        }

        const parsed: ParsedRow[] = result.data.map((rawRow) => {
          const status = (rawRow['status']?.trim() ?? '').toUpperCase()
          return {
            key: allocateRowKey(),
            benefitId: rawRow['benefitId']?.trim() ?? '',
            status: isAllowedStatus(status)
              ? (status as BulkClearStatus)
              : (status as BulkClearStatus | ''),
          }
        })

        if (parsed.length === 0) {
          setParseError('CSV has no data rows.')
          return
        }
        if (parsed.length > MAX_CSV_ROWS) {
          setParseError(
            `CSV contains ${parsed.length} rows. Maximum allowed is ${MAX_CSV_ROWS.toLocaleString()}.`
          )
          return
        }

        setRows(parsed)
        setStep('preview')
      },
      error: (err: Error) => {
        setParseError(`Failed to read file: ${err.message}`)
      },
    })

    e.target.value = ''
  }

  const handleCellChange = (
    key: string,
    field: keyof ParsedRow,
    value: string
  ) => {
    setRows((curr) =>
      curr.map((r) => (r.key !== key ? r : { ...r, [field]: value }))
    )
  }

  const handleAddRow = useCallback(() => {
    setRows((curr) => [...curr, makeEmptyRow()])
  }, [makeEmptyRow])

  const handleDeleteRow = useCallback((key: string) => {
    setRows((curr) => curr.filter((r) => r.key !== key))
  }, [])

  const handleAddManually = useCallback(() => {
    setParseError(null)
    setRows([makeEmptyRow()])
    setStep('preview')
  }, [makeEmptyRow])

  const handleConfirm = useCallback(async () => {
    const validRows = rows.filter(isValidRow)
    const items = validRows.map((row) => ({
      benefitId: row.benefitId,
      status: row.status as BulkClearStatus,
    }))

    const totalBatches = Math.ceil(items.length / CHUNK_SIZE)

    cancelledRef.current = false
    setIsCancelling(false)
    setIsSubmitting(true)
    setBatchProgress({
      sent: 0,
      total: items.length,
      currentBatch: 1,
      totalBatches,
    })

    const allResults: BatchClearPaymentAssignmentResultItemDto[] = []
    const allSkipped: string[] = []
    const chunkErrors: BulkClearResults['chunkErrors'] = []

    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      if (cancelledRef.current) break

      const batchNum = Math.floor(i / CHUNK_SIZE) + 1
      setBatchProgress((prev) => ({ ...prev, currentBatch: batchNum }))

      const chunk = items.slice(i, i + CHUNK_SIZE)
      try {
        const response = await batchClearPaymentAssignmentStatus({
          month: period.month(),
          year: period.year(),
          items: chunk,
        })

        allResults.push(...response)

        // Server returns one entry per *matched* payment assignment, not per
        // submitted benefitId. Anything we sent but didn't get a result for is
        // most likely missing a payment assignment for the chosen month/year.
        const returnedCount = response.length
        if (returnedCount < chunk.length) {
          // We can't map paymentAssignmentId back to benefitId, so we treat
          // every benefitId in the chunk that exceeds the returned count as
          // potentially skipped. Simpler heuristic: just track count diff.
          // For accuracy in the report, list them all (best-effort) — the user
          // can cross-reference against returned successes by paymentAssignmentId.
          allSkipped.push(
            ...chunk.slice(returnedCount).map((item) => item.benefitId)
          )
        }
      } catch (error: any) {
        const raw =
          error.response?.data?.message ||
          error.message ||
          'Batch request failed'
        const msg = typeof raw === 'string' ? raw : JSON.stringify(raw)
        chunkErrors.push({
          benefitIds: chunk.map((item) => item.benefitId),
          message: msg,
        })
      }

      setBatchProgress((prev) => ({
        ...prev,
        sent: Math.min(i + CHUNK_SIZE, items.length),
      }))
    }

    if (
      cancelledRef.current &&
      allResults.length === 0 &&
      chunkErrors.length === 0
    ) {
      setIsSubmitting(false)
      return
    }

    setIsSubmitting(false)
    setResults({
      results: allResults,
      skippedBenefitIds: allSkipped,
      chunkErrors,
    })
    setStep('results')

    const successCount = allResults.filter((r) => r.success).length
    const failureCount = allResults.length - successCount
    if (successCount > 0) {
      notification.success({
        message: `Cleared ${successCount} payment assignment(s)`,
      })
    }
    if (failureCount > 0 || chunkErrors.length > 0) {
      notification.warning({
        message: `${failureCount + chunkErrors.reduce((sum, c) => sum + c.benefitIds.length, 0)} item(s) failed to update`,
      })
    }
  }, [rows, period])

  const handleReset = () => {
    cancelledRef.current = true
    setIsSubmitting(false)
    setIsCancelling(false)
    setStep('upload')
    setRows([])
    setResults(null)
    setParseError(null)
    setBatchProgress({ sent: 0, total: 0, currentBatch: 0, totalBatches: 0 })
  }

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const validCount = useMemo(() => rows.filter(isValidRow).length, [rows])
  const invalidCount = rows.length - validCount

  const previewColumns = [
    {
      title: 'Status',
      key: 'status_icon',
      width: 70,
      render: (_: unknown, row: ParsedRow) => {
        const errors = getRowErrors(row)
        return errors.length === 0 ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined
            style={{ color: '#ff4d4f', fontSize: 16 }}
            title={errors.join('\n')}
          />
        )
      },
    },
    {
      title: 'Benefit ID',
      dataIndex: 'benefitId',
      key: 'benefitId',
      width: 280,
      render: (value: string, row: ParsedRow) => (
        <input
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '2px 6px',
            width: '100%',
            fontSize: 13,
          }}
          value={value}
          onChange={(e) =>
            handleCellChange(row.key, 'benefitId', e.target.value)
          }
        />
      ),
    },
    {
      title: 'Clear Status',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (value: string, row: ParsedRow) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={value || undefined}
          placeholder="Select status"
          onChange={(v: string) => handleCellChange(row.key, 'status', v)}
          options={ALLOWED_STATUSES.map((s) => ({ value: s, label: s }))}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center' as const,
      render: (_: unknown, row: ParsedRow) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isSubmitting}
          onClick={() => handleDeleteRow(row.key)}
          aria-label="Remove row"
        />
      ),
    },
  ]

  // -------------------------------------------------------------------------
  // Upload step
  // -------------------------------------------------------------------------

  if (step === 'upload') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Bulk Clear Payment Assignment Status
          </Title>
          <Text type="secondary">
            Upload a CSV of benefit IDs to mark their payment assignments as
            <Text code style={{ marginLeft: 4, marginRight: 4 }}>
              PAYMENT_CLEARED
            </Text>
            or
            <Text code style={{ marginLeft: 4 }}>
              AP_CONFIRMED
            </Text>
            or
            <Text code style={{ marginLeft: 4 }}>
              BOT_CLEARED
            </Text>
            for the selected payment period. Benefits with no payment assignment
            for the chosen month/year are silently skipped.
          </Text>

          {parseError && (
            <Alert type="error" message={parseError} showIcon closable />
          )}

          <Space wrap align="center">
            <Text strong>Payment period:</Text>
            <DatePicker
              picker="month"
              value={period}
              onChange={(value: Dayjs | null) => {
                if (value) setPeriod(value.startOf('month'))
              }}
              format="MMMM YYYY"
              allowClear={false}
              style={{ minWidth: 180 }}
            />
          </Space>

          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Download CSV Template
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>

            <Button icon={<PlusOutlined />} onClick={handleAddManually}>
              Add Manually
            </Button>
          </Space>

          <Card
            size="small"
            style={{ background: '#fafafa', borderStyle: 'dashed' }}
          >
            <Text strong>CSV format — required columns:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <Text code>benefitId</Text>
                <Text type="secondary"> — required</Text>
              </li>
              <li>
                <Text code>status</Text>
                <Text type="secondary">
                  {' '}
                  — required: PAYMENT_CLEARED or AP_CONFIRMED or BOT_CLEARED
                </Text>
              </li>
            </ul>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Submitted in batches of {CHUNK_SIZE} per request. Maximum{' '}
              {MAX_CSV_ROWS.toLocaleString()} rows per file.
            </Text>
          </Card>
        </Space>
      </Card>
    )
  }

  // -------------------------------------------------------------------------
  // Preview step
  // -------------------------------------------------------------------------

  if (step === 'preview') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>
              Preview &amp; Edit
            </Title>
            <Button onClick={handleReset} disabled={isSubmitting}>
              Back to Upload
            </Button>
          </Space>

          <Space
            style={{ justifyContent: 'space-between', width: '100%' }}
            wrap
          >
            <Space wrap>
              <Tag color="blue">Period: {period.format('MMMM YYYY')}</Tag>
              <Tag color="success">
                <CheckCircleOutlined /> {validCount} valid
              </Tag>
              {invalidCount > 0 && (
                <Tag color="error">
                  <CloseCircleOutlined /> {invalidCount} invalid (will be
                  skipped)
                </Tag>
              )}
            </Space>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddRow}
              disabled={isSubmitting}
            >
              Add Row
            </Button>
          </Space>

          {invalidCount > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${invalidCount} row(s) have validation errors and will be skipped. Fix the highlighted rows or they will not be submitted.`}
            />
          )}

          {validCount === 0 && (
            <Alert
              type="error"
              showIcon
              message={
                rows.length === 0
                  ? 'No rows yet. Click “Add Row” to enter a benefit ID and status, or go back to upload a CSV.'
                  : 'No valid rows to submit. Please fix the errors above.'
              }
            />
          )}

          <Table
            dataSource={rows}
            columns={previewColumns}
            rowKey="key"
            size="small"
            scroll={{ x: 600 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            rowClassName={(row) =>
              isValidRow(row) ? '' : 'ant-table-row-error'
            }
          />

          <style>{`
            .ant-table-row-error td {
              background-color: #fff2f0 !important;
            }
          `}</style>

          {isSubmitting && batchProgress.total > 0 && (
            <>
              <Text type="secondary">
                {isCancelling
                  ? 'Cancelling… waiting for current batch to finish'
                  : `Sending batch ${batchProgress.currentBatch} of ${batchProgress.totalBatches} (${CHUNK_SIZE} items per batch)…`}
              </Text>
              <Progress
                percent={Math.round(
                  (batchProgress.sent / batchProgress.total) * 100
                )}
                format={() =>
                  `${batchProgress.sent} / ${batchProgress.total} rows`
                }
                status={isCancelling ? 'exception' : 'active'}
              />
            </>
          )}

          <Space>
            {isSubmitting ? (
              <Button
                danger
                disabled={isCancelling}
                onClick={() => {
                  cancelledRef.current = true
                  setIsCancelling(true)
                }}
              >
                {isCancelling ? 'Cancelling…' : 'Cancel Upload'}
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={validCount === 0}
                onClick={handleConfirm}
              >
                Confirm &amp; Submit {validCount} Benefit(s)
              </Button>
            )}
            <Button onClick={handleReset} disabled={isSubmitting}>
              Cancel
            </Button>
          </Space>
        </Space>
      </Card>
    )
  }

  // -------------------------------------------------------------------------
  // Results step
  // -------------------------------------------------------------------------

  const successList = (results?.results ?? []).filter((r) => r.success)
  const failureList = (results?.results ?? []).filter((r) => !r.success)
  const chunkErrorRows = (results?.chunkErrors ?? []).flatMap((c) =>
    c.benefitIds.map((benefitId) => ({ benefitId, message: c.message }))
  )
  const skippedRows = (results?.skippedBenefitIds ?? []).map((benefitId) => ({
    benefitId,
  }))

  const successColumns = [
    {
      title: 'Payment Assignment ID',
      dataIndex: 'paymentAssignmentId',
      key: 'paymentAssignmentId',
      render: (id: number) => <Text code>{id}</Text>,
    },
  ]

  const failureColumns = [
    {
      title: 'Payment Assignment ID',
      dataIndex: 'paymentAssignmentId',
      key: 'paymentAssignmentId',
      width: 200,
      render: (id: number) => <Text code>{id}</Text>,
    },
    { title: 'Error', dataIndex: 'error', key: 'error' },
  ]

  const chunkErrorColumns = [
    { title: 'Benefit ID', dataIndex: 'benefitId', key: 'benefitId' },
    { title: 'Error', dataIndex: 'message', key: 'message' },
  ]

  const skippedColumns = [
    { title: 'Benefit ID', dataIndex: 'benefitId', key: 'benefitId' },
  ]

  return (
    <Card style={{ padding: '24px' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Bulk Clear Results — {period.format('MMMM YYYY')}
          </Title>
          <Button type="primary" onClick={handleReset}>
            Start New Batch
          </Button>
        </Space>

        {successList.length > 0 && (
          <Alert
            type="success"
            showIcon
            message={`${successList.length} payment assignment(s) updated successfully`}
          />
        )}

        {(failureList.length > 0 || chunkErrorRows.length > 0) && (
          <Alert
            type="error"
            showIcon
            message={`${failureList.length + chunkErrorRows.length} item(s) failed to update`}
          />
        )}

        {skippedRows.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`${skippedRows.length} benefit ID(s) had no matching payment assignment for ${period.format('MMMM YYYY')} and were skipped.`}
          />
        )}

        {successList.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> Cleared (
                {successList.length})
              </Text>
            }
          >
            <Table
              dataSource={successList}
              columns={successColumns}
              rowKey="paymentAssignmentId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {failureList.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Failures (
                {failureList.length})
              </Text>
            }
          >
            <Table
              dataSource={failureList}
              columns={failureColumns}
              rowKey="paymentAssignmentId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {chunkErrorRows.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Batch
                Request Errors ({chunkErrorRows.length})
              </Text>
            }
          >
            <Table
              dataSource={chunkErrorRows}
              columns={chunkErrorColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {skippedRows.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <WarningOutlined style={{ color: '#faad14' }} /> Skipped — no
                payment assignment found ({skippedRows.length})
              </Text>
            }
          >
            <Table
              dataSource={skippedRows}
              columns={skippedColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}
      </Space>
    </Card>
  )
}
